import { capitalize } from "@mui/material";
import {
  BubbleText,
  Search,
  Subtract,
  Text,
} from "@totalenergiescode/mobility-business-rev-design-system";
import React from "react";
import { useTranslation } from "react-i18next";

import { useUserCompanies } from "@/api/graphql/accounts/getUserCompanies/hooks.ts";
import {
  StyledCurrentCountOnTotalContainer,
  StyledCurrentCountOnTotalText,
} from "@/components/Agents/List";
import { EmptyDataInDB } from "@/components/shared/EmptyComponent";
import { Loading } from "@/components/shared/Loading";
import { RolesProfileIcon } from "@/components/shared/RolesProfileIcon";
import { isRolesProfileCode } from "@/components/shared/RolesProfileIcon/types";
import { useFormattedDate } from "@/hooks/i18n/useFormatDate";
import { ProfileType } from "@/types/profile.ts";
import { iconProps } from "@/types/theme.ts";

import { getUserRoleDisplayedName } from "./helper";
import {
  StyledButton,
  StyledButtonContainer,
  StyledCell,
  StyledCellHeaderText,
  StyledCellText,
  StyledCellWithEndArrow,
  StyledContainer,
  StyledCustomTableContainer,
  StyledDivider,
  StyledRightIcon,
  StyledRoundedLeftContainer,
  StyledRowContainerLink,
  StyledSearchField,
  StyledSearchFieldContainer,
  StyledTable,
  StyledTableHeader,
  StyledTitle,
} from "./styles";

const pageSize = 20;

export const AccountsList: React.FC<{ userId: string }> = ({ userId }) => {
  const { t: tUsers } = useTranslation("users");
  const { t: tProfiles } = useTranslation("profiles");
  const { t: tCompanies } = useTranslation("companies");
  const { t: tCommon } = useTranslation("common");
  const [searchTerm, setSearchTerm] = React.useState("");
  const formattedDate = useFormattedDate();
  const {
    data: listOfCompanies,
    isLoading,
    fetchNextPage,
    hasNextPage,
    totalUserCompanies,
  } = useUserCompanies(userId, searchTerm, { paginateResponse: true });

  return (
    <StyledContainer>
      <StyledRoundedLeftContainer>
        <StyledTitle variant='title2'>
          {tUsers("users.list.subtitle.list")}
        </StyledTitle>
        <StyledDivider />
        <StyledSearchFieldContainer>
          <StyledSearchField
            dataTestId='text-field'
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            placeholder={tUsers("users.list.searchPlaceholder")}
            startIcon={<Search width={18} height={18} />}
          />
        </StyledSearchFieldContainer>

        <StyledCustomTableContainer>
          <StyledTableHeader>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("users.list.data.accountNumber")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("users.list.data.companyName")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("users.list.data.accountProfile")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("users.list.data.status")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("users.list.data.createdTs")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("users.list.data.lastConnection")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>{""}</StyledCellHeaderText>
            </StyledCell>
          </StyledTableHeader>
          <Loading isLoading={isLoading}>
            <StyledTable>
              {listOfCompanies ? (
                listOfCompanies.map((company) => (
                  <StyledRowContainerLink
                    to={`/account/${company.accountId}`}
                    key={company.accountId}
                  >
                    <StyledCell>
                      <StyledCellText variant='text2'>
                        {company.accountNumber}
                      </StyledCellText>
                    </StyledCell>
                    <StyledCell>
                      <StyledCellText variant='text2'>
                        {company.accountName}
                      </StyledCellText>
                    </StyledCell>
                    <StyledCell>
                      {isRolesProfileCode(
                        company?.subscriptions?.[0]?.roleCode ?? "",
                      ) ? (
                        <RolesProfileIcon
                          code={
                            (company?.subscriptions?.[0]
                              ?.roleCode as ProfileType) ?? ""
                          }
                          size={28}
                        />
                      ) : null}
                      <StyledCellText variant='text2'>
                        {getUserRoleDisplayedName({
                          code: company?.subscriptions?.[0]?.roleCode,
                          name: company?.subscriptions?.[0]?.roleName,
                          t: tProfiles,
                        })}
                      </StyledCellText>
                    </StyledCell>
                    <StyledCell>
                      <StyledCellText variant='text2'>
                        <BubbleText
                          text={capitalize(
                            tCompanies(
                              `companies.list.status.${company.status}.label`.toLowerCase(),
                            ),
                          )}
                          variant={
                            company.status.toLowerCase() === "active"
                              ? "green"
                              : "grey"
                          }
                        />
                      </StyledCellText>
                    </StyledCell>
                    <StyledCell>
                      <StyledCellText variant='text2'>
                        {formattedDate(company.invitedTs)}
                      </StyledCellText>
                    </StyledCell>
                    <StyledCell>
                      <StyledCellText variant='text2'>
                        {formattedDate(company?.lastUsedTs)}
                      </StyledCellText>
                    </StyledCell>
                    <StyledCellWithEndArrow>
                      <Text variant='text2'>
                        <StyledRightIcon />
                      </Text>
                    </StyledCellWithEndArrow>
                  </StyledRowContainerLink>
                ))
              ) : (
                <EmptyDataInDB principalText={tCommon("common.noData")} />
              )}
            </StyledTable>
          </Loading>
        </StyledCustomTableContainer>
        <Loading isLoading={isLoading}>
          {hasNextPage ? (
            <StyledButtonContainer>
              <StyledButton
                leftIcon={<Subtract {...iconProps} />}
                variant='secondary'
                buttonText={tCommon("common.pagination.nextPage", { pageSize })}
                isLowercase={true}
                onClick={() => fetchNextPage()}
              ></StyledButton>
            </StyledButtonContainer>
          ) : null}
        </Loading>
        <StyledCurrentCountOnTotalContainer>
          <StyledCurrentCountOnTotalText variant='sub3'>
            {tUsers("users.list.namesWithCurrentCountOnTotal.name", {
              count: listOfCompanies?.length ?? 0,
              total: totalUserCompanies,
            })}
          </StyledCurrentCountOnTotalText>
        </StyledCurrentCountOnTotalContainer>
      </StyledRoundedLeftContainer>
    </StyledContainer>
  );
};
